// Copyright 2017 Sorama B.V.

import { autoinject, observable } from "aurelia-framework";
import { MeasurementWorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog/MeasurementModuleCatalog";
import { MetadataValuePair } from "datatypes/MetadataFields";
import {
    IWorkflowModuleActivate,
    IWorkflowModuleInstance,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { WorkflowPropertyBindingMode } from "libraries/WorkflowFramework/Interfaces/IWorkflowPropertyMetadata";
import { userSetting } from "libraries/WorkflowFramework/UserSettingsAttribute";
import { workflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyAttribute";
import { ShellViewManager } from "modules/shell/App/ShellViewManager";
import { IClientService } from "services/Client/IClientService";
import { NotificationUrgency } from "services/Notification/INotificationService";
import { IProjectManagementService } from "services/Project/IProjectManagementService";
import { IServerStatusService } from "services/Server/IServerStatusService";
import { IRecordingGroupContainerStorageService } from "services/Storage/IRecordingGroupContainerStorageService";
import { IStorageService } from "services/Storage/IStorageService";

/**
 * MODULE OPERATION DESCRIPTION - RecordingMetadataModule
 * The Recording Module lists all details of a recording and enables the user to save the Recording as a Measurement to the database.
 * REQUIRED: a RecordingGroupId
 */
@autoinject
export class RecordingMetadataModule implements IWorkflowModuleActivate {
    //#region Constants
    public metadataSuggestions: string[] = [
        "RPM",
        "Temperature",
        "Operation Mode",
        "Orientation",
        "Speed",
        "Side",
        "Location",
    ];
    //#endregion

    //#region Workflow Properties
    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.oneWay })
    private recordingGroupContainerId: string;

    @userSetting()
    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    private measurementName: string;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.oneWay })
    private isMeasurementComplete: boolean;

    //#endregion

    //#region View Properties
    @observable measurementNotes = "";
    @observable metadataPairs: MetadataValuePair[] = [];

    protected recordingPath: string;

    private containerUpdatePromise: Promise<void> = Promise.resolve();
    private moduleInstance: IWorkflowModuleInstance;

    //#endregion

    constructor(
        private readonly storageService: IStorageService,
        private readonly recordingGroupContainerStorageService: IRecordingGroupContainerStorageService,
        protected readonly projectManagementService: IProjectManagementService,
        protected readonly serverStatusService: IServerStatusService,
        private readonly shellViewManager: ShellViewManager,
        private readonly clientService: IClientService
    ) {}

    public activateModule(moduleInstance: IWorkflowModuleInstance): void {
        this.moduleInstance = moduleInstance;

        this.measurementNameValidate();
    }

    public recordingGroupContainerIdChanged(): void {
        this.loadRecordingGroupDataFromService();
    }

    //#region Module Property Changed Callbacks
    public measurementNameChanged(newValue: string, oldValue: string): void {
        if (newValue !== oldValue && this.recordingGroupContainerId && !!newValue) {
            this.recordingGroupContainerStorageService
                .updateKnownMetadata(this.recordingGroupContainerId, "name", newValue)
                .catch((e: Error) => {
                    this.moduleInstance
                        .createNotification()
                        .withUrgency(NotificationUrgency.Error)
                        .withMessage(e.message)
                        .show();
                });
        }
    }

    private measurementNameValidate(): void {
        if (!this.projectManagementService.currentProject) return;

        this.projectManagementService.measurements.item.forEach((x) => {
            if (x.name === this.measurementName) {
                const postfixRegex = /\(([0-9]+)\)$/;
                const regExResult = postfixRegex.exec(this.measurementName);

                if (regExResult) {
                    const nextPostfix = Number.parseInt(regExResult[1]) + 1;
                    this.measurementName = this.measurementName.slice(0, regExResult.index) + "(" + nextPostfix + ")";
                } else {
                    this.measurementName = this.measurementName + " (1)";
                }
            }
        });
    }

    public measurementNotesChanged(newValue: string, oldValue: string): void {
        if (newValue !== oldValue && this.recordingGroupContainerId && !!newValue) {
            this.recordingGroupContainerStorageService
                .updateKnownMetadata(this.recordingGroupContainerId, "notes", newValue)
                .catch((e: Error) => {
                    this.moduleInstance
                        .createNotification()
                        .withUrgency(NotificationUrgency.Error)
                        .withMessage(e.message)
                        .show();
                });
        }
    }
    //#endregion

    //#region User Input
    public editProject(): void {
        this.shellViewManager.openRightSideBar(MeasurementWorkflowModuleCatalog.ProjectManagementModule);
    }

    public addMetadataEntry(): void {
        this.metadataPairs.push(new MetadataValuePair());
    }

    public deleteMetadataEntry(metadataValuePair: MetadataValuePair): void {
        const index = this.metadataPairs.findIndex((x) => x == metadataValuePair);
        this.metadataPairs.splice(index, 1);

        // Update metadata on disk
        this.metadataChanged();
    }

    private metadataChanged(): void {
        if (this.recordingGroupContainerId) {
            this.recordingGroupContainerStorageService
                .updateKnownMetadata(this.recordingGroupContainerId, "metadata", this.metadataPairs)
                .catch((e: Error) => {
                    this.moduleInstance
                        .createNotification()
                        .withUrgency(NotificationUrgency.Error)
                        .withMessage(e.message)
                        .show();
                });
        }
    }

    public continueMeasurement(): void {
        this.moduleInstance.getWorkflow().addModule(MeasurementWorkflowModuleCatalog.MeasureModule);
    }
    //#endregion

    //#region Helper Methods
    private loadRecordingGroupDataFromService(): void {
        this.containerUpdatePromise = this.containerUpdatePromise.then(() => {
            if (this.recordingGroupContainerId) {
                const promises = [
                    this.storageService
                        .getPath(this.recordingGroupContainerId)
                        .then((x) => (this.recordingPath = x))
                        .catch(() => {}),

                    this.recordingGroupContainerStorageService
                        .load(this.recordingGroupContainerId)
                        .then((x) => {
                            if (x.metadata.name) {
                                // Use measurement name from the container
                                this.measurementName = x.metadata.name;
                            } else {
                                // Update measurement name in the container
                                this.measurementNameChanged(this.measurementName, undefined);
                            }

                            if (x.metadata.notes) {
                                // Use measurement notes from the container
                                this.measurementNotes = x.metadata.notes;
                            } else {
                                // Update measurement notes in the container
                                this.measurementNotesChanged(this.measurementNotes, undefined);
                            }

                            if (x.metadata.metadata && x.metadata.metadata.length > 0) {
                                // Use measurement metadata from the container
                                this.metadataPairs = x.metadata.metadata;
                            } else {
                                // Update measurement metadata in the container
                                this.metadataChanged();
                            }
                        })
                        .catch(() => {
                            this.moduleInstance
                                .createNotification()
                                .withUrgency(NotificationUrgency.Error)
                                .withMessage(
                                    "Measurement with id " + this.recordingGroupContainerId + " does not exist."
                                )
                                .show();
                        }),
                ];

                return Promise.all(promises).then(() => {});
            } else {
                this.measurementName = "";
                this.measurementNotes = "";
                this.metadataPairs = [];
            }
        });
    }

    protected validateKey(key: string): boolean {
        if (!key) return false;

        const regexp = /[\w-\+]+/;

        const match = regexp.exec(key);
        if (match.index !== 0 || match.length !== 1 || match[0].length != key.length) {
            return false;
        }

        return true;
    }

    //#endregion
}
